import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  X, Search, Plus, Minus, Trash2, User, Loader2,
  Stethoscope, TestTube, Syringe, Pill, Activity,
  FileText, ChevronDown, IndianRupee, CheckCircle, Clock, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { searchPatients } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────

interface BillingService {
  id: string;
  name: string;
  price: number;
  category: string;
  is_frequent?: boolean;
  is_active?: boolean;
}

interface Patient {
  id: string;
  name: string;
  phone?: string;
  address?: string;
  age?: number | string;
  gender?: string;
  mrn?: string;
}

interface Doctor {
  id: string;
  name: string;
}

interface BillItem {
  id: string;
  service_id?: string;
  service_name: string;
  category?: string;
  quantity: number;
  unit_price: number;
  total_price: number;
}

interface MedicalBillingModalProps {
  isOpen: boolean;
  onClose: () => void;
  patient?: Patient | null;
  services: BillingService[];
  onCreateInvoice: (invoiceData: {
    items: BillItem[];
    discount: number;
    discountType: 'flat' | 'percent';
    paymentMethod: string;
    paidAmount: number;
    finalTotal: number;
    doctorId?: string;
    patient?: Patient;
  }) => void;
  loading?: boolean;
  clinicId: string;
  doctorId?: string;
  doctors?: Doctor[];
  onRefreshServices?: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function debounce<T extends (...args: any[]) => any>(func: T, wait: number) {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

const PAYMENT_METHODS = ['Cash', 'UPI', 'Card', 'Cheque', 'Insurance'];

const CATEGORY_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  all:          { label: 'All',          icon: FileText,    color: 'hsl(var(--muted-foreground))' },
  frequent:     { label: 'Frequent',     icon: Activity,    color: 'hsl(var(--warning))' },
  consultation: { label: 'Consult',      icon: Stethoscope, color: 'hsl(var(--primary))' },
  diagnostic:   { label: 'Diagnostics',  icon: Activity,    color: 'hsl(var(--info))' },
  lab:          { label: 'Lab',          icon: TestTube,    color: 'hsl(var(--info))' },
  procedure:    { label: 'Procedure',    icon: Syringe,     color: 'hsl(var(--destructive))' },
  medicine:     { label: 'Medicine',     icon: Pill,        color: 'hsl(var(--success))' },
};

function deriveStatus(paid: number, total: number): 'Paid' | 'Partial' | 'Pending' {
  if (total <= 0) return 'Pending';
  if (paid >= total) return 'Paid';
  if (paid > 0) return 'Partial';
  return 'Pending';
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function MedicalBillingModal({
  isOpen,
  onClose,
  patient = null,
  services = [],
  onCreateInvoice,
  loading = false,
  clinicId,
  doctorId,
  doctors = [],
  onRefreshServices,
}: MedicalBillingModalProps) {
  const { currentUser } = useAuth();
  const isStaff = currentUser?.role === 'staff';

  // Bill state
  const [billItems, setBillItems]         = useState<BillItem[]>([]);
  const [discount, setDiscount]           = useState(0);
  const [discountType, setDiscountType]   = useState<'flat' | 'percent'>('flat');
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [paidAmount, setPaidAmount]       = useState(0);
  const [paidAmountEdited, setPaidAmountEdited] = useState(false);
  const [error, setError]                 = useState<string | null>(null);
  const [billDate, setBillDate]           = useState(() => new Date().toISOString().slice(0, 10));

  // Patient search
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(patient ?? null);
  const [patientQuery, setPatientQuery]       = useState('');
  const [patientResults, setPatientResults]   = useState<Patient[]>([]);
  const [patientSearching, setPatientSearching] = useState(false);
  const patientInputRef = useRef<HTMLInputElement>(null);

  // Service search / tab
  const [serviceSearch, setServiceSearch] = useState('');
  const [activeTab, setActiveTab]         = useState('all');
  const [selectedDoctorId, setSelectedDoctorId] = useState(doctorId ?? '');

  // Service dropdown for row add
  const [addingRow, setAddingRow]         = useState(false);
  const [rowSearch, setRowSearch]         = useState('');
  const [rowDropdownOpen, setRowDropdownOpen] = useState(false);
  const rowInputRef = useRef<HTMLInputElement>(null);

  // Sync patient prop
  useEffect(() => { setSelectedPatient(patient ?? null); }, [patient]);
  useEffect(() => { setSelectedDoctorId(doctorId ?? ''); }, [doctorId]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setBillItems([]);
      setDiscount(0);
      setDiscountType('flat');
      setPaymentMethod('Cash');
      setPaidAmount(0);
      setPaidAmountEdited(false);
      setError(null);
      setPatientQuery('');
      setPatientResults([]);
      setBillDate(new Date().toISOString().slice(0, 10));
      setRowSearch('');
      setRowDropdownOpen(false);
      setAddingRow(false);
      if (!patient) setSelectedPatient(null);
    } else {
      setSelectedPatient(patient ?? null);
      setSelectedDoctorId(doctorId ?? '');
    }
  }, [isOpen, patient, doctorId]);

  // ── Calculations ────────────────────────────────────────────────────────────

  const subtotal = useMemo(
    () => billItems.reduce((s, i) => s + (parseFloat(String(i.total_price)) || 0), 0),
    [billItems]
  );

  const discountAmount = useMemo(() => {
    if (discountType === 'percent') return Math.min(subtotal, (subtotal * discount) / 100);
    return Math.min(subtotal, Math.max(0, discount));
  }, [discount, discountType, subtotal]);

  const finalTotal = useMemo(
    () => Math.max(0, subtotal - discountAmount),
    [subtotal, discountAmount]
  );

  // Sync paidAmount to finalTotal when total changes, unless user has manually edited it
  useEffect(() => {
    if (!paidAmountEdited) setPaidAmount(finalTotal);
  }, [finalTotal, paidAmountEdited]);

  const balanceAmount = Math.max(0, finalTotal - paidAmount);
  const paymentStatus = deriveStatus(paidAmount, finalTotal);

  // ── Patient search ──────────────────────────────────────────────────────────

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce(async (q: string) => {
      if (q.trim().length < 2) { setPatientResults([]); setPatientSearching(false); return; }
      setPatientSearching(true);
      try {
        const res = await searchPatients(q, clinicId);
        setPatientResults(Array.isArray(res) ? res.slice(0, 8) : []);
      } catch { setPatientResults([]); }
      finally { setPatientSearching(false); }
    }, 300),
    [clinicId]
  );

  useEffect(() => {
    if (patientQuery.trim().length >= 2) { setPatientSearching(true); debouncedSearch(patientQuery); }
    else setPatientResults([]);
  }, [patientQuery, debouncedSearch]);

  // ── Bill item operations ────────────────────────────────────────────────────

  const addService = (svc: BillingService) => {
    if (!svc.is_active) { setError('This service is inactive'); return; }
    setError(null);
    // Coerce price to number — DB may return strings
    const price = parseFloat(String(svc.price)) || 0;
    const existing = billItems.find(i => i.service_id === svc.id);
    if (existing) {
      updateQty(existing.id, existing.quantity + 1);
    } else {
      setBillItems(prev => [...prev, {
        id: crypto.randomUUID(),
        service_id: svc.id,
        service_name: svc.name,
        category: svc.category,
        quantity: 1,
        unit_price: price,
        total_price: price,
      }]);
    }
    setPaidAmountEdited(false);
  };

  const updateQty = (id: string, qty: number) => {
    if (qty <= 0) { removeItem(id); return; }
    setBillItems(prev => prev.map(i => i.id === id
      ? { ...i, quantity: qty, total_price: qty * (parseFloat(String(i.unit_price)) || 0) }
      : i
    ));
    setPaidAmountEdited(false);
  };

  const updatePrice = (id: string, price: number) => {
    const p = Math.max(0, parseFloat(String(price)) || 0);
    setBillItems(prev => prev.map(i => i.id === id
      ? { ...i, unit_price: p, total_price: i.quantity * p }
      : i
    ));
    setPaidAmountEdited(false);
  };

  const removeItem = (id: string) => {
    setBillItems(prev => prev.filter(i => i.id !== id));
    setPaidAmountEdited(false);
  };

  // ── Filtered services ───────────────────────────────────────────────────────

  const filteredServices = useMemo(() => {
    let list = services.filter(s => s.is_active !== false);
    if (activeTab === 'frequent') list = list.filter(s => s.is_frequent);
    else if (activeTab !== 'all') list = list.filter(s => s.category === activeTab);
    if (serviceSearch) {
      const q = serviceSearch.toLowerCase();
      list = list.filter(s => s.name.toLowerCase().includes(q) || s.category?.toLowerCase().includes(q));
    }
    return list;
  }, [services, activeTab, serviceSearch]);

  const rowFilteredServices = useMemo(() => {
    if (!rowSearch) return services.filter(s => s.is_active !== false).slice(0, 10);
    const q = rowSearch.toLowerCase();
    return services.filter(s => s.is_active !== false && s.name.toLowerCase().includes(q)).slice(0, 10);
  }, [services, rowSearch]);

  const availableTabs = useMemo(() => {
    const cats = new Set(services.map(s => s.category).filter(Boolean));
    const tabs = [{ id: 'all', ...CATEGORY_CONFIG.all }, { id: 'frequent', ...CATEGORY_CONFIG.frequent }];
    cats.forEach(c => {
      if (c !== 'all' && c !== 'frequent' && !tabs.find(t => t.id === c)) {
        tabs.push({ id: c, ...(CATEGORY_CONFIG[c] || { label: c, icon: FileText, color: 'hsl(var(--muted-foreground))' }) });
      }
    });
    return tabs;
  }, [services]);

  // ── Submit ──────────────────────────────────────────────────────────────────

  const handleCreate = () => {
    if (!selectedPatient?.id) { setError('Please select a patient'); return; }
    if (isStaff && !selectedDoctorId) { setError('Please select a doctor'); return; }
    if (billItems.length === 0) { setError('Add at least one service'); return; }
    const invalid = billItems.some(i => !i.service_name || i.unit_price < 0 || i.quantity <= 0);
    if (invalid) { setError('Some items have invalid data'); return; }
    setError(null);
    onCreateInvoice({
      items: billItems,
      discount: discountType === 'percent' ? discountAmount : discount,
      discountType,
      paymentMethod,
      paidAmount: Math.min(paidAmount, finalTotal),
      finalTotal,
      doctorId: selectedDoctorId || undefined,
      patient: selectedPatient,
    });
  };

  if (!isOpen) return null;

  const hasPatient = !!selectedPatient;
  const showDoctorDropdown = isStaff && doctors.length > 0;

  // ── Status badge ────────────────────────────────────────────────────────────
  const statusConfig = {
    Paid:    { color: 'hsl(var(--success))', bg: 'hsl(var(--success) / 0.1)', icon: CheckCircle,   label: 'Paid' },
    Partial: { color: 'hsl(var(--warning))', bg: 'hsl(var(--warning) / 0.1)', icon: AlertCircle,   label: 'Partial' },
    Pending: { color: 'hsl(var(--muted-foreground))', bg: 'hsl(var(--muted))', icon: Clock,         label: 'Pending' },
  }[paymentStatus];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3"
      style={{ background: 'hsl(var(--foreground) / 0.55)', backdropFilter: 'blur(4px)' }}>

      <div className="bg-white rounded-2xl shadow-2xl w-full flex flex-col overflow-hidden"
        style={{ maxWidth: 1100, height: '92vh', fontFamily: "'Manrope', 'Inter', sans-serif" }}>

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-3 border-b"
          style={{ background: 'hsl(var(--primary))', borderColor: 'hsl(var(--border))' }}>

          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div>
              <span className="text-xs font-semibold tracking-widest uppercase text-blue-300">New Bill</span>

              {hasPatient ? (
                <div className="flex items-center gap-2 mt-0.5">
                  <User className="w-3.5 h-3.5 text-white/60 flex-shrink-0" />
                  <span className="text-white font-semibold text-sm truncate capitalize">
                    {selectedPatient!.name}
                  </span>
                  {(selectedPatient!.age || selectedPatient!.gender) && (
                    <span className="text-white/50 text-xs">
                      {selectedPatient!.age ? `${selectedPatient!.age}y` : ''}
                      {selectedPatient!.age && selectedPatient!.gender ? ' · ' : ''}
                      {selectedPatient!.gender ?? ''}
                    </span>
                  )}
                  {selectedPatient!.mrn && (
                    <span className="text-primary/70 text-xs font-mono">#{selectedPatient!.mrn}</span>
                  )}
                  {!patient && (
                    <button onClick={() => { setSelectedPatient(null); setBillItems([]); setPaidAmountEdited(false); }}
                      className="text-white/40 hover:text-white/80 transition-colors ml-1">
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-white/50 text-xs mt-0.5">Select a patient to begin</div>
              )}
            </div>

            {/* Date */}
            <div className="ml-4 flex items-center gap-2">
              <input
                type="date"
                value={billDate}
                onChange={e => setBillDate(e.target.value)}
                className="text-xs text-primary-foreground bg-white/10 border border-white/20 rounded-lg px-2 py-1 focus:outline-none focus:border-primary/60"
              />
              <button
                onClick={() => setBillDate(new Date().toISOString().slice(0, 10))}
                className="text-xs text-primary/70 hover:text-primary-foreground transition-colors font-medium">
                Today
              </button>
            </div>
          </div>

          {/* Total + close */}
          <div className="flex items-center gap-4">
            {billItems.length > 0 && (
              <div className="text-right">
                <div className="text-xs text-white/50">Total</div>
                <div className="text-xl font-bold text-white">₹{finalTotal.toLocaleString('en-IN')}</div>
              </div>
            )}
            <button onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ── Patient search row ── */}
        {!hasPatient && (
          <div className="px-5 py-3 bg-primary/5 border-b border-border relative">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={patientInputRef}
                value={patientQuery}
                onChange={e => setPatientQuery(e.target.value)}
                placeholder="Search patient by name or phone..."
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
                autoFocus
              />
              {patientSearching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />}
            </div>

            {patientResults.length > 0 && (
              <div className="absolute left-5 top-full mt-1 z-10 bg-white border border-gray-200 rounded-xl shadow-xl w-80 max-h-52 overflow-y-auto">
                {patientResults.map(p => (
                  <button key={p.id} onClick={() => { setSelectedPatient(p); setPatientQuery(''); setPatientResults([]); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 transition-colors text-left">
                    <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 text-xs font-semibold">
                      {p.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900 capitalize">{p.name}</div>
                      <div className="text-xs text-gray-500">
                        {p.age ? `${p.age}y` : ''}{p.age && p.gender ? ' · ' : ''}{p.gender ?? ''}{p.phone ? ` · ${p.phone}` : ''}
                        {p.mrn ? ` · #${p.mrn}` : ''}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Doctor dropdown (staff) ── */}
        {showDoctorDropdown && (
          <div className="px-5 py-2 bg-gray-50 border-b flex items-center gap-2">
            <Stethoscope className="w-4 h-4 text-gray-400" />
            <select
              value={selectedDoctorId}
              onChange={e => setSelectedDoctorId(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:border-blue-500">
              <option value="">Select doctor</option>
              {doctors.map(d => (
                <option key={d.id} value={d.id}>{d.name.startsWith('Dr.') ? d.name : `Dr. ${d.name}`}</option>
              ))}
            </select>
          </div>
        )}

        {/* ── Error ── */}
        {error && (
          <div className="mx-5 mt-3 flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}

        {/* ── Main body ── */}
        <div className="flex flex-1 overflow-hidden">

          {/* ══ LEFT: Service catalog ══ */}
          <div className="flex flex-col border-r" style={{ width: 340, minWidth: 300 }}>

            {/* Search + tabs */}
            <div className="p-3 border-b bg-gray-50">
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  value={serviceSearch}
                  onChange={e => setServiceSearch(e.target.value)}
                  placeholder="Search services..."
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                />
              </div>
              <div className="flex gap-1 overflow-x-auto pb-0.5 scrollbar-none">
                {availableTabs.map(tab => {
                  const Icon = tab.icon;
                  const active = activeTab === tab.id;
                  return (
                    <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                      className={cn(
                        'flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-all flex-shrink-0',
                        active ? 'text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      )}
                      style={active ? { background: tab.color } : {}}>
                      <Icon className="w-3 h-3" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Service grid */}
            <div className="flex-1 overflow-y-auto p-3">
              {!hasPatient ? (
                <div className="text-center py-10 text-gray-400">
                  <User className="w-8 h-8 mx-auto mb-2 text-muted-foreground/30" />
                  <p className="text-sm">Select a patient first</p>
                </div>
              ) : filteredServices.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <Search className="w-8 h-8 mx-auto mb-2 text-muted-foreground/30" />
                  <p className="text-sm">No services found</p>
                  {serviceSearch && (
                    <button onClick={() => setServiceSearch('')} className="text-xs text-blue-500 mt-1">Clear search</button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {filteredServices.map(svc => {
                    const inBill = billItems.find(i => i.service_id === svc.id);
                    const catConf = CATEGORY_CONFIG[svc.category] || CATEGORY_CONFIG.all;
                    return (
                      <button
                        key={svc.id}
                        onClick={() => hasPatient && addService(svc)}
                        disabled={!hasPatient}
                        className={cn(
                          'relative p-2.5 rounded-xl border-2 text-left transition-all hover:shadow-md',
                          inBill
                            ? 'border-primary bg-primary/5'
                            : 'border-gray-200 bg-white hover:border-blue-300'
                        )}>
                        {inBill && (
                          <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold shadow">
                            {inBill.quantity}
                          </div>
                        )}
                        <div className={cn('text-xs font-semibold mb-1 leading-tight', inBill ? 'text-primary' : 'text-foreground')}>
                          {svc.name}
                        </div>
                        <div className="text-sm font-bold" style={{ color: catConf.color }}>
                          ₹{svc.price.toLocaleString('en-IN')}
                        </div>
                        <div className="text-xs mt-1 text-gray-400">{catConf.label}</div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ══ RIGHT: Bill table + summary ══ */}
          <div className="flex-1 flex flex-col overflow-hidden">

            {/* ── Bill items table ── */}
            <div className="flex-1 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/40 text-xs text-muted-foreground uppercase tracking-wide">
                    <th className="text-left px-4 py-2.5 w-8">#</th>
                    <th className="text-left px-4 py-2.5">Service</th>
                    <th className="text-center px-3 py-2.5 w-28">Qty</th>
                    <th className="text-right px-4 py-2.5 w-28">Unit Price</th>
                    <th className="text-right px-4 py-2.5 w-28">Total</th>
                    <th className="px-3 py-2.5 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {billItems.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-gray-400">
                        <FileText className="w-8 h-8 mx-auto mb-2 opacity-20" />
                        <p className="text-sm">{hasPatient ? 'Click a service to add it' : 'Select a patient to start billing'}</p>
                      </td>
                    </tr>
                  ) : (
                    billItems.map((item, idx) => (
                      <tr key={item.id} className="border-b hover:bg-muted/20 group">
                        <td className="px-4 py-2 text-muted-foreground/50 text-xs">{idx + 1}</td>
                        <td className="px-4 py-2">
                          <div className="font-medium text-foreground">{item.service_name}</div>
                          {item.category && (
                            <div className="text-xs text-muted-foreground capitalize">{item.category}</div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => updateQty(item.id, item.quantity - 1)}
                              className="w-6 h-6 flex items-center justify-center rounded bg-muted hover:bg-muted/80 text-muted-foreground transition-colors">
                              <Minus className="w-3 h-3" />
                            </button>
                            <input
                              type="number"
                              min={1}
                              value={item.quantity}
                              onChange={e => updateQty(item.id, parseInt(e.target.value) || 1)}
                              className="w-10 text-center text-sm font-semibold border border-gray-200 rounded focus:outline-none focus:border-blue-400 py-0.5"
                            />
                            <button onClick={() => updateQty(item.id, item.quantity + 1)}
                              className="w-6 h-6 flex items-center justify-center rounded bg-muted hover:bg-muted/80 text-muted-foreground transition-colors">
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <span className="text-gray-400 text-xs">₹</span>
                            <input
                              type="number"
                              min={0}
                              value={item.unit_price}
                              onChange={e => updatePrice(item.id, parseFloat(e.target.value) || 0)}
                              className="w-20 text-right text-sm border border-gray-200 rounded focus:outline-none focus:border-blue-400 px-2 py-0.5"
                            />
                          </div>
                        </td>
                        <td className="px-4 py-2 text-right font-semibold text-gray-800">
                          ₹{item.total_price.toLocaleString('en-IN')}
                        </td>
                        <td className="px-3 py-2 text-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => removeItem(item.id)}
                            className="w-6 h-6 flex items-center justify-center rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}

                  {/* Add row */}
                  {hasPatient && (
                    <tr className="border-b">
                      <td className="px-4 py-2 text-muted-foreground/30 text-xs">{billItems.length + 1}</td>
                      <td className="px-4 py-2" colSpan={5}>
                        {addingRow ? (
                          <div className="relative">
                            <input
                              ref={rowInputRef}
                              value={rowSearch}
                              onChange={e => { setRowSearch(e.target.value); setRowDropdownOpen(true); }}
                              onFocus={() => setRowDropdownOpen(true)}
                              onBlur={() => setTimeout(() => { setRowDropdownOpen(false); setAddingRow(false); setRowSearch(''); }, 150)}
                              placeholder="Type service name..."
                              className="w-64 text-sm border-b-2 border-blue-400 focus:outline-none py-1 bg-transparent"
                              autoFocus
                            />
                            {rowDropdownOpen && rowFilteredServices.length > 0 && (
                              <div className="absolute top-full left-0 mt-1 z-20 bg-white border border-gray-200 rounded-xl shadow-xl w-72 max-h-48 overflow-y-auto">
                                {rowFilteredServices.map(svc => (
                                  <button key={svc.id}
                                    onMouseDown={() => { addService(svc); setAddingRow(false); setRowSearch(''); setRowDropdownOpen(false); }}
                                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-primary/5 text-left">
                                    <div>
                                      <div className="text-sm font-medium text-gray-800">{svc.name}</div>
                                      <div className="text-xs text-muted-foreground capitalize">{svc.category}</div>
                                    </div>
                                    <div className="text-sm font-semibold text-primary">₹{svc.price.toLocaleString('en-IN')}</div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <button onClick={() => { setAddingRow(true); setRowSearch(''); setRowDropdownOpen(true); }}
                            className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-colors">
                            <Plus className="w-3.5 h-3.5" />
                            Add service
                          </button>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ── Summary + payment ── */}
            {billItems.length > 0 && (
              <div className="border-t bg-muted/30 px-5 py-4 space-y-4">

                {/* Financials row */}
                <div className="flex items-start gap-6">

                  {/* Subtotal / discount / total */}
                  <div className="flex-1 space-y-1.5">
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>Subtotal</span>
                      <span className="font-medium text-foreground">₹{subtotal.toLocaleString('en-IN')}</span>
                    </div>

                    {/* Discount row */}
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-gray-500">Discount</span>
                      <div className="flex items-center gap-1.5">
                        {/* Flat / % toggle */}
                        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                          <button onClick={() => setDiscountType('flat')}
                            className={cn('px-2 py-0.5 text-xs font-medium transition-colors',
                              discountType === 'flat' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}>
                            ₹
                          </button>
                          <button onClick={() => setDiscountType('percent')}
                            className={cn('px-2 py-0.5 text-xs font-medium transition-colors',
                              discountType === 'percent' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}>
                            %
                          </button>
                        </div>
                        <input
                          type="number"
                          min={0}
                          max={discountType === 'percent' ? 100 : subtotal}
                          value={discount}
                          onChange={e => setDiscount(parseFloat(e.target.value) || 0)}
                          className="w-20 text-right text-sm border border-gray-200 rounded-lg px-2 py-0.5 focus:outline-none focus:border-blue-400"
                        />
                        {discountAmount > 0 && (
                          <span className="text-xs text-red-500 font-medium">-₹{discountAmount.toLocaleString('en-IN')}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-between text-base font-bold border-t border-border pt-1.5">
                      <span>Total</span>
                      <span className="text-primary font-bold">₹{finalTotal.toLocaleString('en-IN')}</span>
                    </div>
                  </div>

                  {/* Payment section */}
                  <div className="flex-1 space-y-2">
                    {/* Payment methods */}
                    <div className="flex flex-wrap gap-1.5">
                      {PAYMENT_METHODS.map(m => (
                        <button key={m} onClick={() => setPaymentMethod(m)}
                          className={cn(
                            'px-3 py-1 rounded-lg text-xs font-medium border transition-all',
                            paymentMethod === m
                              ? 'text-primary-foreground border-transparent shadow-sm'
                              : 'text-muted-foreground border-border hover:border-primary/50 hover:text-primary'
                          )}
                          style={paymentMethod === m ? { background: 'hsl(var(--primary))', borderColor: 'hsl(var(--primary))' } : {}}>
                          {m}
                        </button>
                      ))}
                    </div>

                    {/* Received amount */}
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-muted-foreground whitespace-nowrap">Received</label>
                      <div className="relative flex-1">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                        <input
                          type="number"
                          min={0}
                          max={finalTotal}
                          value={paidAmount}
                          onChange={e => {
                            const v = Math.min(finalTotal, Math.max(0, parseFloat(e.target.value) || 0));
                            setPaidAmount(v);
                            setPaidAmountEdited(true);
                          }}
                          className="w-full pl-7 pr-3 py-1.5 text-sm border-2 rounded-lg focus:outline-none transition-colors font-semibold"
                          style={{ borderColor: paidAmount >= finalTotal ? 'hsl(var(--success))' : paidAmount > 0 ? 'hsl(var(--warning))' : 'hsl(var(--border))' }}
                        />
                      </div>

                      {/* Live status badge */}
                      <div className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold flex-shrink-0"
                        style={{ background: statusConfig.bg, color: statusConfig.color }}>
                        <statusConfig.icon className="w-3 h-3" />
                        {statusConfig.label}
                      </div>
                    </div>

                    {balanceAmount > 0 && (
                      <div className="text-xs text-warning flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Balance due: ₹{balanceAmount.toLocaleString('en-IN')}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-2 pt-1">
                  <button onClick={onClose} disabled={loading}
                    className="flex-none px-4 py-2 text-sm font-medium text-muted-foreground border border-border rounded-xl hover:bg-muted transition-colors disabled:opacity-50">
                    Cancel
                  </button>
                  <button
                    onClick={handleCreate}
                    disabled={loading || billItems.length === 0 || !hasPatient}
                    className="flex-1 py-2 text-sm font-semibold text-white rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                    style={{ background: loading || billItems.length === 0 || !hasPatient ? 'hsl(var(--muted-foreground))' : 'hsl(var(--primary))' }}>
                    {loading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        {paymentStatus === 'Paid'
                          ? `Paid — ₹${finalTotal.toLocaleString('en-IN')}`
                          : paymentStatus === 'Partial'
                          ? `Partial — ₹${paidAmount.toLocaleString('en-IN')} of ₹${finalTotal.toLocaleString('en-IN')}`
                          : `Create Bill — ₹${finalTotal.toLocaleString('en-IN')} (Pending)`
                        }
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}